/**
 * Route Preloader Utility
 *
 * Intelligent route and component preloading system for AmalQ that optimizes
 * loading performance based on user behavior patterns and device capabilities.
 *
 * Features:
 * - Critical route preloading on app initialization
 * - Hover-based preloading for desktop navigation
 * - Intersection Observer for content preloading
 * - Network-aware preloading decisions
 * - Memory management and cleanup
 * - Performance monitoring and analytics
 */


// Preload priority levels
export enum PreloadPriority {
  CRITICAL = 'critical',    // Must preload (home, login, register)
  HIGH = 'high',           // Preload on hover/interaction
  MEDIUM = 'medium',       // Preload when visible
  LOW = 'low'              // Optional preloading
}

// Route preload configuration
interface RoutePreloadConfig {
  path: string;
  priority: PreloadPriority;
  component: () => Promise<{ default: React.ComponentType<Record<string, unknown>> }>;
  timeout?: number;
  conditions?: {
    deviceTier?: 'low-end' | 'mid-range' | 'high-end' | 'mobile';
    networkSpeed?: 'slow' | 'fast';
    userIntent?: boolean;
  };
}

// Preload state management
interface PreloadState {
  [key: string]: {
    status: 'idle' | 'loading' | 'loaded' | 'error';
    promise?: Promise<{ default: React.ComponentType<Record<string, unknown>> }>;
    timestamp?: number;
    size?: number;
  };
}

/**
 * RoutePreloader Class - Core preloading engine
 */
export class RoutePreloader {
  private static instance: RoutePreloader;
  private preloadState: PreloadState = {};
  private observers: Map<string, IntersectionObserver> = new Map();
  private hoverTimeouts: Map<string, NodeJS.Timeout> = new Map();
  private networkObserver: PerformanceObserver | null = null;
  private memoryPressure = false;

  // Default preload configurations
  private routeConfigs: RoutePreloadConfig[] = [
    {
      path: '/',
      priority: PreloadPriority.CRITICAL,
      component: () => import('../components/pages/HomePage'),
      timeout: 3000,
    },
    {
      path: '/login',
      priority: PreloadPriority.CRITICAL,
      component: () => import('../components/pages/login/LoginPage'),
      timeout: 2000,
    },
    {
      path: '/register',
      priority: PreloadPriority.CRITICAL,
      component: () => import('../components/pages/sign-up/SignUpPage'),
      timeout: 2000,
    },
    {
      path: '/about-us',
      priority: PreloadPriority.HIGH,
      component: () => import('../components/pages/about-us/AboutUsPage'),
      timeout: 4000,
    },
    // /how-it-works removed -- merged into /about-us
    {
      path: '/blog',
      priority: PreloadPriority.MEDIUM,
      component: () => import('../components/pages/blog/BlogListPage'),
      timeout: 3000,
    },
    {
      path: '/contact',
      priority: PreloadPriority.MEDIUM,
      component: () => import('../components/pages/contact-us/ContactUsPage'),
      timeout: 2000,
    },
  ];

  private constructor() {
    this.initializeNetworkMonitoring();
    this.initializeMemoryMonitoring();
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): RoutePreloader {
    if (!RoutePreloader.instance) {
      RoutePreloader.instance = new RoutePreloader();
    }
    return RoutePreloader.instance;
  }

  /**
   * Initialize network performance monitoring
   */
  private initializeNetworkMonitoring(): void {
    if ('PerformanceObserver' in window) {
      try {
        this.networkObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const navigation = entries.find(entry => entry.entryType === 'navigation') as PerformanceNavigationTiming;

          if (navigation) {
            // Estimate connection speed based on navigation timing
            const connectionSpeed = this.estimateConnectionSpeed(navigation);
            this.adjustPreloadingStrategy(connectionSpeed);
          }
        });

        this.networkObserver.observe({ entryTypes: ['navigation'] });
      } catch (error) {
        // Network monitoring not supported (silent)
      }
    }
  }

  /**
   * Initialize memory pressure monitoring
   */
  private initializeMemoryMonitoring(): void {
    if ('memory' in performance) {
      // Monitor memory usage (Chrome only)
      const checkMemoryPressure = (): void => {
        const memInfo = (performance as unknown as { memory: { totalJSHeapSize: number; usedJSHeapSize: number } }).memory;
        const pressureThreshold = memInfo.totalJSHeapSize * 0.8; // 80% threshold

        this.memoryPressure = memInfo.usedJSHeapSize > pressureThreshold;

        if (this.memoryPressure) {
          this.cleanupLowPriorityPreloads();
        }
      };

      // Check memory every 30 seconds
      setInterval(checkMemoryPressure, 30000);
    }
  }

  /**
   * Estimate connection speed based on navigation timing
   */
  private estimateConnectionSpeed(navigation: PerformanceNavigationTiming): 'slow' | 'fast' {
    const totalTime = navigation.loadEventEnd - navigation.fetchStart;
    return totalTime > 2000 ? 'slow' : 'fast';
  }

  /**
   * Adjust preloading strategy based on connection speed
   */
  private adjustPreloadingStrategy(connectionSpeed: 'slow' | 'fast'): void {
    if (connectionSpeed === 'slow') {
      // Reduce preloading on slow connections
      this.routeConfigs = this.routeConfigs.filter(config =>
        config.priority === PreloadPriority.CRITICAL
      );
    }
  }

  /**
   * Preload critical routes on app initialization
   */
  public async preloadCriticalRoutes(deviceTier?: 'high-end' | 'mid-range' | 'low-end' | 'mobile'): Promise<void> {
    try {
      const tier = deviceTier || 'mid-range'; // Default fallback

      const criticalRoutes = this.routeConfigs.filter(config => {
        // Check device tier compatibility
        if (config.conditions?.deviceTier && config.conditions.deviceTier !== tier) {
          return false;
        }

        return config.priority === PreloadPriority.CRITICAL;
      });

      // Preload critical routes with staggered timing to avoid overwhelming the network
      const preloadPromises = criticalRoutes.map((config, index) => {
        return new Promise<void>((resolve) => {
          setTimeout(async () => {
            try {
              await this.preloadRoute(config.path, tier);
              resolve();
            } catch (error) {
              // Failed to preload (silent)
              resolve(); // Don't fail the entire batch
            }
          }, index * 500); // Stagger by 500ms
        });
      });

      await Promise.allSettled(preloadPromises);
    } catch (error) {
      // Critical routes preloading failed (silent)
      // Continue silently - this should never break the app
    }
  }

  /**
   * Preload a specific route
   */
  public async preloadRoute(path: string, deviceTier?: 'high-end' | 'mid-range' | 'low-end' | 'mobile'): Promise<void> {
    const config = this.routeConfigs.find(c => c.path === path);
    if (!config) return;

    // Check if already loaded or loading
    if (this.preloadState[path]?.status === 'loaded' || this.preloadState[path]?.status === 'loading') {
      return;
    }

    // Mark as loading
    this.preloadState[path] = { status: 'loading', timestamp: Date.now() };

    try {
      // Create preload promise with timeout
      const preloadPromise = config.component();
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Preload timeout')), config.timeout || 5000);
      });

      const module = await Promise.race([preloadPromise, timeoutPromise]);

      // Mark as loaded
      this.preloadState[path] = {
        status: 'loaded',
        timestamp: Date.now(),
        size: this.estimateModuleSize(module as { default: React.ComponentType<Record<string, unknown>> }),
      };

      // Log successful preload for analytics
      this.logPreloadSuccess(path, config.priority, deviceTier);

    } catch (error) {
      this.preloadState[path] = {
        status: 'error',
        timestamp: Date.now(),
      };
      // Preload failed (silent)
    }
  }

  /**
   * Preload route on hover (desktop optimization)
   */
  public preloadOnHover(path: string, deviceTier?: 'high-end' | 'mid-range' | 'low-end' | 'mobile'): void {
    const tier = deviceTier || 'mid-range'; // Default fallback

    // Only preload on hover for medium/high-end devices
    if (tier === 'low-end') return;

    // Clear existing timeout
    const existingTimeout = this.hoverTimeouts.get(path);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Set new timeout (preload after 100ms of hovering)
    const timeout = setTimeout(() => {
      this.preloadRoute(path);
      this.hoverTimeouts.delete(path);
    }, 100);

    this.hoverTimeouts.set(path, timeout);
  }

  /**
   * Cancel hover preload
   */
  public cancelHoverPreload(path: string): void {
    const timeout = this.hoverTimeouts.get(path);
    if (timeout) {
      clearTimeout(timeout);
      this.hoverTimeouts.delete(path);
    }
  }

  /**
   * Preload route when element becomes visible
   */
  public preloadOnVisible(path: string, element: Element): void {
    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.preloadRoute(path);
            observer.disconnect();
            this.observers.delete(path);
          }
        });
      },
      {
        rootMargin: '50px', // Start preloading 50px before visible
        threshold: 0.1,
      }
    );

    observer.observe(element);
    this.observers.set(path, observer);
  }

  /**
   * Estimate module size for memory management
   */
  private estimateModuleSize(module: { default: React.ComponentType<Record<string, unknown>> }): number {
    // Rough estimation based on component count and complexity
    try {
      const componentString = JSON.stringify(module);
      return componentString.length * 2; // Rough byte estimation
    } catch {
      return 0;
    }
  }

  /**
   * Clean up low-priority preloads when memory pressure is detected
   */
  private cleanupLowPriorityPreloads(): void {
    const lowPriorityRoutes = Object.keys(this.preloadState).filter(path => {
      const config = this.routeConfigs.find(c => c.path === path);
      return config?.priority === PreloadPriority.LOW;
    });

    lowPriorityRoutes.forEach(path => {
      delete this.preloadState[path];
    });

    // Clean up observers
    this.observers.forEach((observer, path) => {
      const config = this.routeConfigs.find(c => c.path === path);
      if (config?.priority === PreloadPriority.LOW) {
        observer.disconnect();
        this.observers.delete(path);
      }
    });
  }

  /**
   * Log successful preload for analytics
   */
  private logPreloadSuccess(_path: string, _priority: PreloadPriority, _deviceTier?: 'high-end' | 'mid-range' | 'low-end' | 'mobile'): void {
    // Log to analytics service (implement based on your analytics setup)
    // Send to analytics (silent - no console output)
    // Analytics data structure prepared but not used for clean console
  }

  /**
   * Get preload status for a route
   */
  public getPreloadStatus(path: string): PreloadState[string] | null {
    return this.preloadState[path] || null;
  }

  /**
   * Get all preload states
   */
  public getAllPreloadStates(): PreloadState {
    return { ...this.preloadState };
  }

  /**
   * Clear all preload states and cleanup
   */
  public clearAll(): void {
    this.preloadState = {};

    // Clear timeouts
    this.hoverTimeouts.forEach(timeout => clearTimeout(timeout));
    this.hoverTimeouts.clear();

    // Disconnect observers
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();

    // Disconnect network observer
    if (this.networkObserver) {
      this.networkObserver.disconnect();
      this.networkObserver = null;
    }
  }
}

// Export singleton instance
export const routePreloader = RoutePreloader.getInstance();

// React hook for using route preloader
export const useRoutePreloader = (): {
  preloadRoute: (path: string) => Promise<void>;
  preloadOnHover: (path: string) => void;
  cancelHoverPreload: (path: string) => void;
  preloadOnVisible: (path: string, element: Element) => void;
  getPreloadStatus: (path: string) => PreloadState[string] | null;
  getAllPreloadStates: () => PreloadState;
} => {
  return {
    preloadRoute: (path: string): Promise<void> => routePreloader.preloadRoute(path),
    preloadOnHover: (path: string): void => routePreloader.preloadOnHover(path),
    cancelHoverPreload: (path: string): void => routePreloader.cancelHoverPreload(path),
    preloadOnVisible: (path: string, element: Element): void => routePreloader.preloadOnVisible(path, element),
    getPreloadStatus: (path: string): PreloadState[string] | null => routePreloader.getPreloadStatus(path),
    getAllPreloadStates: (): PreloadState => routePreloader.getAllPreloadStates(),
  };
};

// Utility function to preload critical routes on app start
export const initializeRoutePreloading = async (): Promise<void> => {
  try {
    // Wait for initial page load
    if (document.readyState === 'loading') {
      await new Promise(resolve => {
        window.addEventListener('DOMContentLoaded', resolve);
      });
    }

    // Get device capabilities for proper preloading
    const deviceTier = 'mid-range'; // Safe default for initialization

    // Small delay to prioritize initial page rendering
    setTimeout(() => {
      routePreloader.preloadCriticalRoutes(deviceTier).catch((_error) => {
        // Route preloading failed (silent)
        // Don't let preloading failures break the app
      });
    }, 100);
  } catch (error) {
    // Route preloader initialization failed (silent)
    // Continue without preloading - pages will still work
  }
};
